import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@material-ui/core';
import { wait } from 'globals/utils';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsFormFullWidthContainer from 'ui-elements/form-elements/FsFormFullWidthContainer';
import FsNextArrowIcon from 'ui-elements/icons/FsNextArrow';
import FsPhoneNumberInput from 'ui-elements/form-elements/FsPhoneNumberInput';
import defaultLocales from 'globals/locales/default';

export interface FsRegisterPhoneNumberFormDataProps {
  phoneNumber: string;
  formattedPhoneNumber?: string;
}

interface FsRegisterPhoneNumberFormProps
  extends FsRegisterPhoneNumberFormDataProps {
  onClose?: () => void;
  onGoNext?: (props: FsRegisterPhoneNumberFormDataProps) => void;
}

const FsRegisterPhoneNumberForm: React.FC<FsRegisterPhoneNumberFormProps> = ({
  onClose,
  onGoNext,
  ...defaults
}) => {
  const formEl = useRef<HTMLFormElement>(null);
  const [phoneNumber, setPhoneNumber] = useState<string>(defaults.phoneNumber);
  const [formattedPhoneNumber, setFormattedPhoneNumber] = useState<string>(
    defaults.phoneNumber
  );
  const [pending, setPending] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const handleSubmit = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault();
    setPending(true);
    // eslint-disable-next-line no-console
    console.log('Save changes', {
      phoneNumber,
      formattedPhoneNumber,
    });
    await wait(1000);
    setPending(false);
    if (onClose) onClose();
    if (onGoNext) onGoNext({ phoneNumber, formattedPhoneNumber });
  };

  const fields = [
    {
      required: true,
      name: 'phone',
      value: phoneNumber,
      label: defaultLocales.phoneNumber,
      autoFocus: true,
      placeholder: `${defaults.phoneNumber} *`,
      onChange: (
        value: string,
        country: never,
        e: never,
        formattedValue: string
      ): void => {
        setPhoneNumber(value);
        setFormattedPhoneNumber(formattedValue);
      },
      onKeyDown: async (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && isFormValid) {
          await handleSubmit();
        }
      },
      inputProps: {
        name: 'phone',
        required: true,
        autoFocus: true,
      },
    },
  ];

  useEffect(() => {
    const isDefaultFormValid = !!formEl?.current?.checkValidity();
    const isPhoneValid = phoneNumber.length >= 5;
    setIsFormValid(isDefaultFormValid && isPhoneValid);
  }, [phoneNumber]);

  return (
    <Box>
      <form onSubmit={handleSubmit} ref={formEl}>
        {fields.map(({ name, ...otherProps }) => (
          <FsFormFullWidthContainer key={name}>
            <FsPhoneNumberInput {...{ ...otherProps, name }} />
          </FsFormFullWidthContainer>
        ))}
        <FsPendingButton
          type="submit"
          variant="contained"
          color="primary"
          pending={pending}
          disabled={!isFormValid}
          endIcon={<FsNextArrowIcon />}
          fullWidth
          spaceBetween
        >
          {defaultLocales.continue}
        </FsPendingButton>
      </form>
    </Box>
  );
};

export default FsRegisterPhoneNumberForm;
